import type { InferOutput } from 'valibot'
import { array, nullish, object, union } from 'valibot'
import {
  booleanData,
  datetimeData,
  imagesData,
  numericData,
  richTextData,
  selectionData,
  singleLineData,
  videosData,
} from '~/utils/crystallize/component-schemas'

/**
 * @see https://app.crystallize.com/@juridika-test/no-nb/settings/shapes/piece/content-block
 */
export const contentBlockPiece = object({
  title: nullish(singleLineData),
  subtitle: nullish(singleLineData),
  text: nullish(richTextData),
  image: nullish(imagesData),
})

export type ContentBlockData = InferOutput<typeof contentBlockPiece>

/**
 * @see https://app.crystallize.com/@juridika-test/no-nb/settings/shapes/piece/template
 */
export const templatePiece = object({
  title: nullish(singleLineData),
  updatedat: nullish(datetimeData),
})

/**
 * @see https://app.crystallize.com/@juridika-test/no-nb/settings/shapes/piece/person
 */
export const personPiece = object({
  name: nullish(singleLineData),
  title: nullish(singleLineData),
  image: nullish(imagesData),
  description: nullish(richTextData),
  contactInfo: nullish(
    singleChunk(
      object({
        phoneNumber: nullish(singleLineData),
        email: nullish(singleLineData),
      }),
    ),
  ),
})

export type PersonData = InferOutput<typeof personPiece>

/**
 * @see https://app.crystallize.com/@juridika-test/no-nb/settings/shapes/piece/quote
 */
export const quotePiece = object({
  text: nullish(richTextData),
  author: nullish(personPiece),
})

export type QuoteData = InferOutput<typeof quotePiece>

/**
 * @see https://app.crystallize.com/@juridika-test/no-nb/settings/shapes/piece/law-quote
 */
export const lawQuotePiece = object({
  text: nullish(richTextData),
  showSectionSymbol: booleanData,
})

export type LawQuoteData = InferOutput<typeof lawQuotePiece>

/**
 * @see https://app.crystallize.com/@juridika-test/no-nb/settings/shapes/piece/fact-box
 */
export const factBoxPiece = object({
  title: nullish(singleLineData),
  facts: nullish(
    array(object({
      text: nullish(singleLineData),
    })),
  ),
})

export type FactBoxData = InferOutput<typeof factBoxPiece>

/**
 * @see https://app.crystallize.com/@juridika-test/no-nb/settings/shapes/piece/cta
 */
export const ctaPiece = object({
  title: nullish(singleLineData),
  text: nullish(richTextData),
  action: nullish(
    array(object({
      link: nullish(singleLineData),
      label: nullish(singleLineData),
    })),
  ),
  graphic: nullish(
    union([
      selectionData,
      imagesData,
      videosData,
    ]),
  ),
})

export type CtaData = InferOutput<typeof ctaPiece>

/**
 * @see https://app.crystallize.com/@juridika-test/no-nb/settings/shapes/piece/usp-list
 */
export const uspListPiece = object({
  title: nullish(singleLineData),
  usp: nullish(
    array(
      object({
        title: nullish(singleLineData),
        text: nullish(richTextData),
      }),
    ),
  ),
})

export type UspListData = InferOutput<typeof uspListPiece>

/**
 * @see https://app.crystallize.com/@juridika-test/no-nb/settings/shapes/piece/cards-section
 */
export const cardsSectionPiece = object({
  title: nullish(singleLineData),
  maxCount: nullish(numericData),
  showOverviewLink: nullish(booleanData),
})

export type CardsSectionData = InferOutput<typeof cardsSectionPiece>

/**
 * @see https://app.crystallize.com/@juridika-test/no-nb/settings/shapes/piece/link-button
 */
export const linkButtonPiece = object({
  label: nullish(singleLineData),
  action: nullish(
    union([
      selectionData,
      singleLineData,
    ]),
  ),
})

export type LinkButtonData = InferOutput<typeof linkButtonPiece>

/**
 * @see https://app.crystallize.com/@juridika-test/no-nb/settings/shapes/piece/hubspot-form
 */
export const hubspotFormPiece = object({
  title: nullish(singleLineData),
  text: nullish(richTextData),
  guid: uuidSchema,
})

export type HubspotFormData = InferOutput<typeof hubspotFormPiece>

/**
 * @see https://app.crystallize.com/@juridika-test/no-nb/settings/shapes/piece/testimonials
 */
export const testimonialsPiece = object({
  title: nullish(singleLineData),
  subtitle: nullish(singleLineData),
  logos: nullish(imagesData),
  quote: nullish(quotePiece),
})

export type TestimonialsData = InferOutput<typeof testimonialsPiece>

/**
 * @see https://app.crystallize.com/@juridika-test/no-nb/settings/shapes/piece/accordion
 */
export const accordionPiece = object({
  items: nullish(
    array(
      object({
        summary: singleLineData,
        content: nullish(richTextData),
      }),
    ),
  ),
})

export type AccordionData = InferOutput<typeof accordionPiece>

/**
 * @see https://app.crystallize.com/@juridika-test/no-nb/settings/shapes/piece/faq
 */
export const faqPiece = object({
  title: nullish(singleLineData),
  description: nullish(richTextData),
  aside: nullish(richTextData),
  questions: nullish(accordionPiece),
})

export type FaqData = InferOutput<typeof faqPiece>

/**
 * @see https://app.crystallize.com/@juridika-test/no-nb/settings/shapes/piece/people
 */
export const peoplePiece = object({
  title: nullish(singleLineData),
  description: nullish(richTextData),
  secondaryTitle: nullish(singleLineData),
  persons: safeArray(
    object({
      person: nullish(personPiece),
    }),
  ),
})

export type PeopleData = InferOutput<typeof peoplePiece>

/**
 * @see https://app.crystallize.com/@juridika-test/no-nb/settings/shapes/piece/cookie-table
 */
export const cookieTablePiece = object({
  title: nullish(singleLineData),
  description: nullish(richTextData),
})

export type CookieTableData = InferOutput<typeof cookieTablePiece>

/**
 * @see https://app.crystallize.com/@juridika-test/no-nb/settings/shapes/piece/stats
 */
export const statsPiece = object({
  values: safeArray(
    object({
      label: singleLineData,
      value: numericData,
      exact: booleanData,
    }),
  ),
})

export type StatsData = InferOutput<typeof statsPiece>
